import React, { useContext } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Layout from '../../components/layout/login';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import * as yup from 'yup';
import { Formik, Form } from 'formik';
import styles from './style.module.scss';
import { postData, getData } from '../../services/ajax/';
import { AuthContext } from '../../context/auth';
import { LoadingModalContext } from '../../components/modal/loading';
import { AlertModalContext } from '../../components/modal/alert';
import { setYupLocaleES } from '../../const/yup';

setYupLocaleES();
const ingresarFormSchema = yup.object().shape({
    email: yup.string()
        .email("Debe ser un email válido")
        .required("El email es requerido"),
    clave: yup.string()
        .required("La contraseña es requerida")
        .min(8, "La contraseña debe tener al menos 8 caracteres"),
});

function Page() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { updateUsuario } = useContext(AuthContext);
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);

    const onLoginSubmit = async (values, { setSubmitting }) => {
        loadingModal.setOpen(true);
        setSubmitting(true);

        try {
            // Hacer login al endpoint auth/login
            const loginResponse = await postData("auth/login", {
                email: values.email,
                contrasenia: values.clave
            });

            // Verificar si hay error en la respuesta
            if (loginResponse.error) {
                alertModal.showModal("Datos incorrectos", loginResponse.error);
                loadingModal.setOpen(false);
                setSubmitting(false);
                return;
            }

            // Obtener el token de la respuesta
            const token = loginResponse.data?.token;
            if (!token) {
                alertModal.showModal("Error", "No se recibió el token de autenticación");
                loadingModal.setOpen(false);
                setSubmitting(false);
                return;
            }

            // Llamar a auth/me para obtener los datos completos del usuario
            const meResponse = await getData("auth/me", token);

            if (meResponse.error) {
                alertModal.showModal("Error", meResponse.error);
                loadingModal.setOpen(false);
                setSubmitting(false);
                return;
            }

            // Combinar los datos del usuario con el token y almacenar
            const usuario = {
                ...meResponse.data,
                token: token
            };
            updateUsuario(usuario);

            // Redirigir al dashboard después del login exitoso
            navigate("/dashboard", { replace: true });
        } catch (error) {
            alertModal.showModal("Error", error.message || "Ocurrió un error al intentar iniciar sesión");
        } finally {
            loadingModal.setOpen(false);
            setSubmitting(false);
        }
    }
    return (
        <Layout>
            <Grid container direction="column">
                <h2>Ingreso</h2>
                <Formik
                    initialValues={{
                        email: '',
                        clave: '',
                    }}
                    validationSchema={ingresarFormSchema}
                    onSubmit={onLoginSubmit}
                >
                    {({ errors, touched, isSubmitting, values, handleChange, handleBlur }) => (
                        <Form className={styles.form}>
                            <TextField
                                variant="standard"
                                label="Email"
                                type="email"
                                autoComplete="username"
                                value={values.email}
                                onChange={handleChange('email')}
                                onBlur={handleBlur('email')}
                                error={errors.email && touched.email}
                                helperText={errors.email && touched.email ? errors.email : ""}
                                fullWidth
                            />

                            <TextField
                                variant="standard"
                                label="Contraseña"
                                type="password"
                                autoComplete="current-password"
                                value={values.clave}
                                onChange={handleChange('clave')}
                                onBlur={handleBlur('clave')}
                                error={errors.clave && touched.clave}
                                helperText={errors.clave && touched.clave ? errors.clave : ""}
                                fullWidth
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Ingresando..." : "Ingresar"}
                            </Button>

                            <Box
                                component={RouterLink}
                                to="/recuperar-acceso"
                                aria-label="No puedo acceder, restablecer contraseña con CUIT y correo"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1.25,
                                    py: 1.5,
                                    px: 1.5,
                                    borderRadius: 2,
                                    textDecoration: "none",
                                    color: "primary.main",
                                    border: `1px dashed ${alpha(theme.palette.primary.main, 0.45)}`,
                                    background: alpha(theme.palette.primary.main, 0.04),
                                    transition: "box-shadow 0.25s ease, transform 0.2s ease, background-color 0.2s ease",
                                    "&:hover": {
                                        background: alpha(theme.palette.primary.main, 0.1),
                                        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
                                        transform: "translateY(-1px)",
                                    },
                                    "&:focus-visible": {
                                        outline: `2px solid ${theme.palette.primary.main}`,
                                        outlineOffset: 2,
                                    },
                                }}
                            >
                                <LockResetRoundedIcon color="primary" fontSize="small" />
                                <Typography variant="body2" fontWeight={700} align="center">
                                    ¿No podés acceder? Te ayudamos a recuperar la contraseña
                                </Typography>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Grid>
        </Layout>
    );
}

export default Page;